import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Euro, Activity, Bike, ListChecks, Send, Bell, FileSignature, ClipboardCheck, Plus, FileSpreadsheet, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function Kpi({ label, value, icon: Icon, testId, accent = false, subtitle = null }) {
  return (
    <div className={`bg-zinc-900/60 border border-zinc-800 ${accent ? "border-t-2 border-t-[#FFD700]" : ""} p-5 rounded-md`}
         data-testid={testId}>
      <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="font-display font-bold text-4xl mt-3 tracking-tight">{value}</div>
      {subtitle && (
        <div className="text-[11px] text-zinc-500 mt-2 font-mono" data-testid={`${testId}-subtitle`}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [telegram, setTelegram] = useState({ subscribers: [], loading: true });
  const [tgBusy, setTgBusy] = useState(false);
  const now = new Date();
  const [exportYear, setExportYear] = useState(now.getFullYear());
  const [exportMonth, setExportMonth] = useState(now.getMonth() + 1);
  const [exporting, setExporting] = useState(null);
  const [tgEnabled, setTgEnabled] = useState(true);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/admin/telegram/subscribers")
      .then((r) => setTelegram({ subscribers: r.data, loading: false }))
      .catch(() => setTelegram({ subscribers: [], loading: false }));
    api.get("/admin/telegram/status")
      .then((r) => setTgEnabled(!!r.data?.admin_notifications_enabled))
      .catch(() => {});
  }, []);

  const toggleNotifications = async () => {
    const next = !tgEnabled;
    try {
      await api.post("/admin/telegram/toggle", { enabled: next });
      setTgEnabled(next);
      toast.success(
        i18n.language === "en"
          ? (next ? "Telegram notifications enabled" : "Telegram notifications paused")
          : (next ? "Notifiche Telegram attive" : "Notifiche Telegram in pausa")
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const subscribe = async () => {
    setTgBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/subscribe");
      setTelegram((s) => ({ ...s, subscribers: [...s.subscribers, ...(data.added || [])] }));
      const added = (data.added || []).length;
      toast.success(
        i18n.language === "en"
          ? `${added} new chat${added === 1 ? "" : "s"} subscribed · total ${data.total_subscribers}`
          : `${added} nuova${added === 1 ? "" : " chat"} registrate · totale ${data.total_subscribers}`
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setTgBusy(false); }
  };

  const sendTest = async () => {
    setTgBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/test");
      toast.success(`Test inviato a ${data.delivered} chat`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setTgBusy(false); }
  };

  const downloadExport = async (kind, useMonth) => {
    setExporting(kind);
    try {
      const params = {};
      if (kind !== "customers") {
        params.year = exportYear;
        if (useMonth) params.month = exportMonth;
      }
      const res = await api.get(`/admin/export/${kind}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const suffix = kind === "customers" ? "" : `_${exportYear}${useMonth ? `-${String(exportMonth).padStart(2, "0")}` : ""}`;
      a.download = `rentlupi_${kind}${suffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(i18n.language === "en" ? "Excel file downloaded" : "File Excel scaricato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setExporting(null); }
  };

  if (stats === null) return <div className="text-zinc-400">{t("common.loading")}</div>;

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-2">{t("admin.control_room")}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">{t("admin.dashboard")}</h1>
        <p className="text-zinc-400 mt-2">{t("admin.panel_subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi
          label={t("admin.kpi_earnings")}
          value={`€${(stats.total_earnings || 0).toFixed(2)}`}
          icon={Euro}
          testId="admin-kpi-earnings"
          accent
          subtitle={
            (stats.online_earnings !== undefined || stats.desk_earnings !== undefined)
              ? (i18n.language === "en"
                  ? `online €${(stats.online_earnings || 0).toFixed(0)} · desk €${(stats.desk_earnings || 0).toFixed(0)}`
                  : `online €${(stats.online_earnings || 0).toFixed(0)} · banco €${(stats.desk_earnings || 0).toFixed(0)}`)
              : null
          }
        />
        <Kpi label={i18n.language === "en" ? "Active rentals" : "Noleggi attivi"} value={stats.active_rentals ?? 0} icon={Activity} testId="admin-kpi-active" />
        <Kpi label={t("admin.kpi_available")} value={`${stats.available_scooters ?? 0}/${stats.total_scooters ?? 0}`} icon={Bike} testId="admin-kpi-available" />
        <Kpi label={t("admin.kpi_bookings")} value={stats.total_bookings ?? 0} icon={ListChecks} testId="admin-kpi-bookings" />
      </div>

      {/* Expiring documents alert */}
      {(stats.expiring_count ?? 0) > 0 && (
        <div className="border border-red-500/40 bg-red-500/[0.06] rounded-xl p-5" data-testid="alert-expiring-panel">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-400 animate-pulse" />
            <h2 className="font-display text-xl font-bold text-red-300">
              {i18n.language === "en" ? "Vehicles needing attention" : "Mezzi in allerta scadenze"}
            </h2>
            <span className="ml-auto bg-red-500/20 text-red-300 text-xs font-bold px-2 py-1 rounded-full" data-testid="alert-expiring-count">
              {stats.expiring_count}
            </span>
          </div>
          <p className="text-xs text-zinc-400 mb-3">
            {i18n.language === "en"
              ? "Insurance or revision expiring within 15 days (or already expired)."
              : "Polizza assicurativa o revisione in scadenza entro 15 giorni (o già scadute)."}
          </p>
          <div className="space-y-2">
            {stats.expiring_vehicles.slice(0, 8).map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 bg-zinc-950/40 border border-zinc-800 rounded-lg px-3 py-2 text-sm" data-testid={`alert-vehicle-${v.id}`}>
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    {v.vehicle_type === "auto" ? "🚗" : "🛵"} {v.model}
                    <span className="text-xs text-zinc-500 font-mono uppercase">{v.plate}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs">
                    {v.alerts.map((a, idx) => (
                      <span key={`${v.id || v.plate}-${a.kind}-${idx}`} className={a.days < 0 ? "text-red-400" : a.days <= 7 ? "text-orange-400" : "text-amber-300"}>
                        {a.kind === "insurance_expiry" ? "🛡 Assic." : "🔧 Revis."}: {a.days < 0 ? `scaduta ${Math.abs(a.days)}g fa` : `${a.days}g`}
                      </span>
                    ))}
                  </div>
                </div>
                <a href="/admin/fleet" className="text-[#FFD700] hover:underline text-xs">{i18n.language === "en" ? "Fleet →" : "Flotta →"}</a>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-zinc-800 rounded-md p-6">
        <h2 className="font-display text-2xl font-bold mb-2">{t("admin.welcome")}</h2>
        <p className="text-zinc-400 text-sm">{t("admin.welcome_text")}</p>
      </div>

      {/* Desk quick actions — walk-in customer onboarding */}
      <div className="rounded-2xl border border-[#FFD700]/30 bg-gradient-to-br from-[#FFD700]/[0.06] to-transparent p-6" data-testid="desk-quick-actions">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#FFD700]/15 border border-[#FFD700]/40 grid place-items-center">
            <ClipboardCheck className="w-5 h-5 text-[#FFD700]" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#FFD700]">{i18n.language === "en" ? "At the desk" : "Al banco"}</p>
            <h2 className="font-display text-2xl font-bold leading-tight">
              {i18n.language === "en" ? "Walk-in customer" : "Cliente in sede"}
            </h2>
            <p className="text-zinc-400 text-sm mt-1">
              {i18n.language === "en"
                ? "Create a contract from scratch for a customer arriving without booking. Sign on tablet, mark as paid/unpaid, generate delivery report."
                : "Crea un contratto da zero per un cliente che arriva senza prenotazione. Firma su tablet, segna come pagato/non pagato, genera verbale di consegna."}
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <Link to="/admin/contracts/new" data-testid="dash-new-contract">
            <div className="rounded-xl bg-[#FFD700] hover:bg-[#FACC15] text-zinc-950 p-4 transition cursor-pointer group">
              <Plus className="w-5 h-5 mb-2" />
              <p className="font-semibold">{i18n.language === "en" ? "New contract" : "Nuovo contratto"}</p>
              <p className="text-xs opacity-75 mt-0.5">{i18n.language === "en" ? "3-step wizard + signature" : "Wizard 3 step + firma"}</p>
            </div>
          </Link>
          <Link to="/admin/contracts" data-testid="dash-all-contracts">
            <div className="rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 p-4 transition cursor-pointer">
              <FileSignature className="w-5 h-5 mb-2 text-[#FFD700]" />
              <p className="font-semibold text-zinc-100">{i18n.language === "en" ? "All contracts" : "Tutti i contratti"}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{i18n.language === "en" ? "Search & manage" : "Ricerca e gestione"}</p>
            </div>
          </Link>
          <Link to="/admin/customers" data-testid="dash-customers">
            <div className="rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 p-4 transition cursor-pointer">
              <ClipboardCheck className="w-5 h-5 mb-2 text-[#FFD700]" />
              <p className="font-semibold text-zinc-100">{i18n.language === "en" ? "Customers" : "Clienti"}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{i18n.language === "en" ? "Digital file & docs" : "Fascicolo digitale"}</p>
            </div>
          </Link>
          <Link to="/admin/contract-template" data-testid="dash-template">
            <div className="rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 p-4 transition cursor-pointer">
              <ClipboardCheck className="w-5 h-5 mb-2 text-[#FFD700]" />
              <p className="font-semibold text-zinc-100">{i18n.language === "en" ? "Edit template" : "Modifica modello"}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{i18n.language === "en" ? "Legal clauses" : "Clausole legali"}</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Excel Export for commercialista */}
      <div className="border border-zinc-800 rounded-md p-6" data-testid="export-excel-panel">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#FFD700]" />
            <h2 className="font-display text-2xl font-bold">
              {i18n.language === "en" ? "Excel Export — Accountant" : "Export Excel — Commercialista"}
            </h2>
          </div>
        </div>
        <p className="text-zinc-400 text-sm mb-4">
          {i18n.language === "en"
            ? "Download spreadsheets with customers, manual contracts and online bookings for your accountant. Filter by year/month."
            : "Scarica fogli Excel con clienti, contratti manuali e prenotazioni online da inviare al commercialista. Filtra per anno/mese."}
        </p>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">{i18n.language === "en" ? "Year" : "Anno"}</label>
            <select value={exportYear} onChange={(e) => setExportYear(parseInt(e.target.value, 10))}
                    className="block bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-zinc-100 text-sm mt-1"
                    data-testid="export-year-select">
              {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">{i18n.language === "en" ? "Month" : "Mese"}</label>
            <select value={exportMonth} onChange={(e) => setExportMonth(parseInt(e.target.value, 10))}
                    className="block bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-zinc-100 text-sm mt-1"
                    data-testid="export-month-select">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(2000, m - 1, 1).toLocaleString(i18n.language === "en" ? "en-GB" : "it-IT", { month: "long" })}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <Button onClick={() => downloadExport("contracts", true)} disabled={exporting === "contracts"}
                  className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold justify-start"
                  data-testid="export-contracts-month-button">
            <Download className="w-4 h-4 mr-2" />
            {exporting === "contracts"
              ? (i18n.language === "en" ? "Preparing…" : "Preparazione…")
              : (i18n.language === "en" ? "Manual contracts (month)" : "Contratti manuali (mese)")}
          </Button>
          <Button onClick={() => downloadExport("bookings", true)} disabled={exporting === "bookings"}
                  className="bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-100 justify-start"
                  data-testid="export-bookings-month-button">
            <Download className="w-4 h-4 mr-2" />
            {exporting === "bookings"
              ? (i18n.language === "en" ? "Preparing…" : "Preparazione…")
              : (i18n.language === "en" ? "Online bookings (month)" : "Prenotazioni online (mese)")}
          </Button>
          <Button onClick={() => downloadExport("customers", false)} disabled={exporting === "customers"}
                  variant="outline"
                  className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800 text-zinc-200 justify-start"
                  data-testid="export-customers-button">
            <Download className="w-4 h-4 mr-2" />
            {exporting === "customers"
              ? (i18n.language === "en" ? "Preparing…" : "Preparazione…")
              : (i18n.language === "en" ? "All customers" : "Tutti i clienti")}
          </Button>
          <Button onClick={() => downloadExport("contracts", false)} disabled={exporting === "contracts"}
                  variant="outline"
                  className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800 text-zinc-200 justify-start"
                  data-testid="export-contracts-year-button">
            <Download className="w-4 h-4 mr-2" />
            {i18n.language === "en" ? "Manual contracts (year)" : "Contratti manuali (anno)"}
          </Button>
          <Button onClick={() => downloadExport("bookings", false)} disabled={exporting === "bookings"}
                  variant="outline"
                  className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800 text-zinc-200 justify-start"
                  data-testid="export-bookings-year-button">
            <Download className="w-4 h-4 mr-2" />
            {i18n.language === "en" ? "Online bookings (year)" : "Prenotazioni online (anno)"}
          </Button>
        </div>
      </div>

      {/* Telegram notifications */}
      <div className="border border-zinc-800 rounded-md p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#FFD700]" />
            <h2 className="font-display text-2xl font-bold">
              {i18n.language === "en" ? "Telegram Notifications" : "Notifiche Telegram"}
            </h2>
            <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${tgEnabled ? "bg-green-500/15 text-green-400 border-green-500/40" : "bg-red-500/15 text-red-400 border-red-500/40"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${tgEnabled ? "bg-green-400" : "bg-red-400"}`}></span>
              {tgEnabled ? "ON" : "OFF"}
            </span>
          </div>
          <span className="text-xs text-zinc-500" data-testid="tg-subscriber-count">
            {telegram.subscribers.length} {i18n.language === "en" ? "chats" : "chat"}
          </span>
        </div>
        <p className="text-zinc-400 text-sm mb-4">
          {i18n.language === "en"
            ? "Open the Rent Lupi Telegram bot, send /start, then click 'Register chat' below. You'll receive notifications for bookings, cancellations and day-before reminders for pickups & returns."
            : "Apri il bot Telegram Rent Lupi, invia /start, poi clicca 'Registra chat' qui sotto. Riceverai notifiche per prenotazioni, cancellazioni e promemoria 1 giorno prima delle uscite e dei rientri."}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={toggleNotifications}
                  className={tgEnabled
                    ? "bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/40"
                    : "bg-green-500/15 hover:bg-green-500/25 text-green-300 border border-green-500/40"}
                  data-testid="tg-toggle-button">
            {tgEnabled
              ? (i18n.language === "en" ? "Pause notifications" : "Sospendi notifiche")
              : (i18n.language === "en" ? "Resume notifications" : "Riattiva notifiche")}
          </Button>
          <Button onClick={subscribe} disabled={tgBusy}
                  className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold"
                  data-testid="tg-subscribe-button">
            <Send className="w-4 h-4 mr-2" />
            {i18n.language === "en" ? "Register chat" : "Registra chat"}
          </Button>
          <Button onClick={sendTest} disabled={tgBusy || telegram.subscribers.length === 0}
                  variant="outline"
                  className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800 text-zinc-200"
                  data-testid="tg-test-button">
            {i18n.language === "en" ? "Send test" : "Invia test"}
          </Button>
        </div>
        {telegram.subscribers.length > 0 && (
          <div className="mt-4 text-xs text-zinc-500">
            <span className="text-zinc-400">{i18n.language === "en" ? "Subscribed:" : "Registrati:"}</span>{" "}
            {telegram.subscribers.map((s) => s.username ? `@${s.username}` : s.first_name || s.chat_id).join(" · ")}
          </div>
        )}
      </div>
    </div>
  );
}
